import {
  ContactSlugEnum,
  CountryEnum,
  DateModeEnum,
  DatePicker,
  FormInput,
  FormSelect,
  getCountryData,
  getDate,
} from '@systemeio/ui'
import {
  contactFilterConditionTitles,
  contactFilterContactStateTitles,
  contactFilterDateConditionTitles,
  contactFilterTagConditionTitle,
} from 'modules/contacts/constants/contact-filter-titles'
import {
  ContactFilterConditionEnum,
  ContactFilterContactStateEnum,
  ContactFilterDateConditionEnum,
  ContactFilterTagConditionEnum,
  ContactSearchFieldEnum,
} from 'modules/contacts/enums/contact-enum'
import { ContactBulkData } from 'modules/contacts/types/contact-menu-interface'
import { useTagsAll } from 'modules/tags/hooks/use-tags-all'
import React, { useEffect, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import useUser from 'shared/hooks/use-user'
import { getToday } from 'shared/utils/get-dates'
import { CloseIcon } from '@systemeio/ui/icons'

interface ContactFilterDefaultSearchProps {
  searchValue: string
  length: number
  index: number
  deleteFilter: (slug: string, index: number) => void
  onChangeSearchValue: (value: string) => void
  slug: string
  condition: ContactFilterConditionEnum | undefined
  onChangeCondition: (data: ContactFilterConditionEnum) => void
}

interface DefaultSearchDataInterface {
  id: ContactFilterConditionEnum
  caption: ContactFilterConditionEnum
}

export const ContactFilterDefaultSearch = ({
  searchValue,
  length,
  index,
  deleteFilter,
  onChangeSearchValue,
  slug,
  condition,
  onChangeCondition,
}: ContactFilterDefaultSearchProps) => {
  const { t } = useLocoTranslation()
  const defaultSearchData: DefaultSearchDataInterface[] = Object.values(ContactFilterConditionEnum)
    .map(el => ({ id: el, caption: t(contactFilterConditionTitles[el]) }))
    .filter(el =>
      slug === ContactSlugEnum.Email ? el.id !== ContactFilterConditionEnum.not_set : true,
    )

  return (
    <div
      className={`flex items-center justify-between gap-3 ${
        length > 1 && length - 1 !== index && 'border-text-secondary/30 border-b pb-3'
      }`}
    >
      <div className={`flex w-full flex-col gap-3`}>
        <FormSelect
          withoutCloseIcon
          data={defaultSearchData}
          value={condition}
          onChange={data => data && onChangeCondition(data)}
        />
        {slug === ContactSlugEnum.PhoneNumber ? (
          // TODO open with form-input-phone
          // <FormInputPhone
          //   placeholder={t('dashboard.contacts.filter.enter_search_value')}
          //   onChange={onChangeSearchValue}
          //   value={searchValue}
          // />
          <FormInput
            placeholder={t('dashboard.contacts.filter.enter_search_value')}
            value={searchValue}
            onChange={e => onChangeSearchValue(e.target.value)}
            disabled={condition === ContactFilterConditionEnum.not_set}
          />
        ) : (
          <FormInput
            placeholder={t('dashboard.contacts.filter.enter_search_value')}
            value={searchValue}
            onChange={e => onChangeSearchValue(e.target.value)}
            disabled={condition === ContactFilterConditionEnum.not_set}
          />
        )}
      </div>
      {length > 1 && (
        <button
          type={'button'}
          className={'group outline-hidden'}
          onClick={e => {
            e.preventDefault()
            deleteFilter(slug, index)
          }}
        >
          <CloseIcon
            className={
              'group-focus-visible:fill-text-primary fill-text-tertiary hover:fill-text-primary'
            }
          />
        </button>
      )}
    </div>
  )
}

interface ContactFilterCountrySearchProps {
  length: number
  index: number
  deleteFilter: (slug: ContactSlugEnum | keyof typeof ContactSearchFieldEnum, index: number) => void
  searchValue: CountryEnum | undefined
  onChangeSearchValue: (value: CountryEnum | undefined) => void
  slug: ContactSlugEnum.Country
}

export const ContactFilterCountrySearch = ({
  length,
  index,
  deleteFilter,
  searchValue,
  onChangeSearchValue,
  slug,
}: ContactFilterCountrySearchProps) => {
  const { t } = useLocoTranslation()
  const { user } = useUser()
  return (
    <div
      className={`flex items-center justify-between gap-3 ${
        length > 1 && length - 1 !== index && 'border-text-secondary/30 border-b pb-3'
      }`}
    >
      <div className={`flex w-full flex-col gap-3`}>
        <FormSelect
          key={'country-filter'}
          placeholder={t('dashboard.contacts.filter.choose_country')}
          data={getCountryData(user?.dashboardLocale)}
          value={searchValue}
          onChange={data => onChangeSearchValue(data)}
        />
      </div>
      {length > 1 && (
        <button
          type={'button'}
          onClick={e => {
            e.preventDefault()
            deleteFilter(slug, index)
          }}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  )
}

interface ContactFilterTagSearchProps {
  length: number
  index: number
  deleteFilter: (slug: ContactSlugEnum | ContactSearchFieldEnum, index: number) => void
  searchValue: number | undefined
  onChangeSearchValue: (value: number | undefined) => void
  slug: ContactSearchFieldEnum.tag
  condition: ContactFilterTagConditionEnum | undefined
  onChangeCondition: (data: ContactFilterTagConditionEnum) => void
}

interface TagSearchDataInterface {
  id: ContactFilterTagConditionEnum
  caption: ContactFilterTagConditionEnum
}

export const ContactFilterTagSearch = ({
  length,
  index,
  deleteFilter,
  searchValue,
  onChangeSearchValue,
  slug,
  condition,
  onChangeCondition,
}: ContactFilterTagSearchProps) => {
  const { t } = useLocoTranslation()
  const tagConditionData: TagSearchDataInterface[] = Object.values(
    ContactFilterTagConditionEnum,
  ).map(el => ({ id: el, caption: t(contactFilterTagConditionTitle[el]) }))
  const { tags } = useTagsAll()
  const [tagData, setTagData] = useState<ContactBulkData[] | undefined>(undefined)
  useEffect(() => {
    if (tags) {
      setTagData(tags.items.map(el => ({ id: el.id, caption: el.name })))
    }
  }, [tags])

  return (
    <div
      className={`flex items-center justify-between gap-3 ${
        length > 1 && length - 1 !== index && 'border-text-secondary/30 border-b pb-3'
      }`}
    >
      <div className={`flex w-full flex-col gap-3`}>
        <FormSelect
          withoutCloseIcon
          data={tagConditionData}
          value={condition}
          onChange={data => data && onChangeCondition(data)}
        />
        <FormSelect
          isPreFetching={!tags}
          placeholder={t('dashboard.contacts.choose_tag')}
          disabled={condition === ContactFilterTagConditionEnum.without}
          data={tagData}
          value={searchValue}
          onChange={data => onChangeSearchValue(data)}
        />
      </div>
      {length > 1 && (
        <button
          type={'button'}
          onClick={e => {
            e.preventDefault()
            deleteFilter(slug, index)
          }}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  )
}

interface ContactFilterDateSearchProps {
  length: number
  index: number
  deleteFilter: (slug: ContactSlugEnum | ContactSearchFieldEnum, index: number) => void
  searchValue: string | null
  onChangeSearchValue: (value: string | null) => void
  slug: ContactSearchFieldEnum.created_at
  condition: ContactFilterDateConditionEnum | undefined
  onChangeCondition: (data: ContactFilterDateConditionEnum) => void
}

interface DateSearchDataInterface {
  id: ContactFilterDateConditionEnum
  caption: ContactFilterDateConditionEnum
}

export const ContactFilterDateSearch = ({
  length,
  index,
  deleteFilter,
  searchValue,
  onChangeSearchValue,
  slug,
  condition,
  onChangeCondition,
}: ContactFilterDateSearchProps) => {
  const { t } = useLocoTranslation()
  const { user, intlLocale } = useUser()
  const dateData: DateSearchDataInterface[] = Object.values(ContactFilterDateConditionEnum).map(
    el => ({ id: el, caption: t(contactFilterDateConditionTitles[el]) }),
  )
  return (
    <div
      className={`flex items-center justify-between gap-3 ${
        length > 1 && length - 1 !== index && 'border-text-secondary/30 border-b pb-3'
      }`}
    >
      <div className={`flex w-full flex-col gap-3`}>
        <FormSelect
          withoutCloseIcon
          data={dateData}
          value={condition}
          onChange={data => data && onChangeCondition(data)}
        />
        <DatePicker
          mode={
            condition === ContactFilterDateConditionEnum.less_than
              ? DateModeEnum.DateTill
              : DateModeEnum.DateSince
          }
          maxDate={getToday(user?.timezone)}
          minDate={getDate(user?.createdAt, user?.timezone)}
          value={searchValue}
          onChange={date => onChangeSearchValue(date)}
          timezone={user?.timezone}
          createdAt={user?.createdAt}
          intlLocale={intlLocale}
        />
      </div>
      {length > 1 && (
        <button
          type={'button'}
          onClick={e => {
            e.preventDefault()
            deleteFilter(slug, index)
          }}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  )
}

interface ContactFilterEmailActivitySearchProps {
  length: number
  index: number
  deleteFilter: (slug: keyof typeof ContactSlugEnum | ContactSearchFieldEnum, index: number) => void
  searchValue: string | null
  onChangeSearchValue: (value: string | null) => void
  slug: ContactSearchFieldEnum.last_activity_date
}

export const ContactFilterEmailActivitySearch = ({
  length,
  index,
  deleteFilter,
  searchValue,
  onChangeSearchValue,
  slug,
}: ContactFilterEmailActivitySearchProps) => {
  const { t } = useLocoTranslation()
  const { user, intlLocale } = useUser()
  return (
    <div
      className={`flex items-center justify-between gap-3 ${
        length > 1 && length - 1 !== index && 'border-text-secondary/30 border-b pb-3'
      }`}
    >
      <div className={`flex w-full flex-col gap-3`}>
        <DatePicker
          maxDate={getToday(user?.timezone)}
          minDate={getDate(user?.createdAt, user?.timezone)}
          placeholder={t('global.select_date')}
          label={t('dashboard.contacts.filter.state_no_activity_since')}
          value={searchValue}
          onChange={date => onChangeSearchValue(date)}
          timezone={user?.timezone}
          createdAt={user?.createdAt}
          intlLocale={intlLocale}
        />
      </div>
      {length > 1 && (
        <button
          type={'button'}
          onClick={e => {
            e.preventDefault()
            deleteFilter(slug, index)
          }}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  )
}

interface ContactFilterContactStateSearchProps {
  length: number
  index: number
  deleteFilter: (slug: ContactSlugEnum | ContactSearchFieldEnum, index: number) => void
  slug: ContactSearchFieldEnum.state
  condition: ContactFilterContactStateEnum | undefined
  onChangeCondition: (data?: ContactFilterContactStateEnum) => void
}

interface ContactStateSearchDataInterface {
  id: ContactFilterContactStateEnum
  caption: ContactFilterContactStateEnum
}

export const ContactFilterContactStateSearch = ({
  index,
  length,
  deleteFilter,
  slug,
  condition,
  onChangeCondition,
}: ContactFilterContactStateSearchProps) => {
  const { t } = useLocoTranslation()
  const contactStateData: ContactStateSearchDataInterface[] = Object.values(
    ContactFilterContactStateEnum,
  ).map(el => ({ id: el, caption: t(contactFilterContactStateTitles[el]) }))
  return (
    <div
      className={`flex items-center justify-between gap-3 ${
        length > 1 && length - 1 !== index && 'border-text-secondary/30 border-b pb-3'
      }`}
    >
      <div className={`flex w-full flex-col gap-3`}>
        <FormSelect
          withoutCloseIcon
          data={contactStateData}
          value={condition}
          onChange={date => onChangeCondition(date)}
        />
      </div>
      {length > 1 && (
        <button
          type={'button'}
          onClick={e => {
            e.preventDefault()
            deleteFilter(slug, index)
          }}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  )
}

interface ContactFilterAffiliateSearchProps {
  length: number
  index: number
  deleteFilter: (slug: keyof typeof ContactSlugEnum | ContactSearchFieldEnum, index: number) => void
  slug: ContactSearchFieldEnum.affiliate
  searchValue: string
  onChangeSearchValue: (value: string) => void
}

export const ContactFilterAffiliateSearch = ({
  length,
  index,
  deleteFilter,
  slug,
  searchValue,
  onChangeSearchValue,
}: ContactFilterAffiliateSearchProps) => {
  const { t } = useLocoTranslation()
  return (
    <div
      className={`flex items-center justify-between gap-3 ${
        length > 1 && length - 1 !== index && 'border-text-secondary/30 border-b pb-3'
      }`}
    >
      <div className={`flex w-full flex-col gap-3`}>
        <FormInput
          placeholder={t('dashboard.contacts.filter.affiliate_id_or_email')}
          value={searchValue}
          onChange={e => onChangeSearchValue(e.target.value)}
        />
      </div>
      {length > 1 && (
        <button
          type={'button'}
          onClick={e => {
            e.preventDefault()
            deleteFilter(slug, index)
          }}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  )
}
